import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit, inject } from '@angular/core';
import { FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { EnvironmentInjector } from '@next/core-lib/environment';
import { SiteState } from '@next/core-lib/site';
import { select, Store } from '@ngrx/store';
import { combineLatest, Observable, ReplaySubject } from 'rxjs';
import { startWith, takeUntil } from 'rxjs/operators';
import {
  isPrefilling,
  selectNitrogenCalculationSettingTogglesByAdviceId,
  selectNitrogenCalculationSettingTogglesByAdvices,
} from 'src/app/advice/store/selectors';
import {
  CropFieldAdvice,
  FertilizationSettingWithName,
  NitrogenCalculationSettingToggles,
  OrganicMatterSupply,
} from 'src/app/shared/models';

import { getCropYear, selectCropFieldAdvicesByIds } from 'src/app/store/selectors';
import { Environment } from 'src/environments/environment';

import { QuickEditDialogModel } from '../edit-all-fertilization-settings-dialog.model';

@Component({
  selector: 'app-fertilization-setting-detail',
  templateUrl: './fertilization-setting-detail.component.html',
  styleUrls: ['./fertilization-setting-detail.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class FertilizationSettingDetailComponent implements OnInit, OnDestroy {
  private readonly store = inject<Store<SiteState>>(Store);
  private readonly env = inject<Environment>(EnvironmentInjector);

  @Input() formGroup: FormGroup;
  @Input() data: QuickEditDialogModel;
  @Input() fertilizationSetting: FertilizationSettingWithName;
  @Input() adviceId: number;

  destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);

  isPrefilling$ = this.store.pipe(select(isPrefilling));
  nitrogenCalculationSettingToggles$: Observable<NitrogenCalculationSettingToggles>;
  cropId$: Observable<number>;
  cropYear$ = this.store.pipe(select(getCropYear));

  organicMatterSupplyKeys: string[];
constructor() {}
  ngOnInit(): void {
    this.registerFormEvents();

    this.organicMatterSupplyKeys = Object.keys(OrganicMatterSupply);

    this.nitrogenCalculationSettingToggles$ = this.store.pipe(
      select(selectNitrogenCalculationSettingTogglesByAdviceId(this.adviceId)),
    );

    this.cropId$ = this.formGroup.controls.cropId.valueChanges.pipe(
      startWith(this.formGroup.controls.cropId.value),
    );

    this.setIsAfterTornGrassAutomatically();
    this.setShouldBeUsedInLegalStandardAutomatically();
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  setIsAfterTornGrassAutomatically() {
    const isPrecedingCrop$ = this.formGroup.get('isPrecedingCrop');
    const cropId$ = this.formGroup.controls.precedingCropSettings.get('cropId');

    combineLatest([
      this.store.pipe(
        select(selectNitrogenCalculationSettingTogglesByAdvices),
        startWith({ shouldShowAfterTornGrassToggle: false }),
      ),
      isPrecedingCrop$.valueChanges.pipe(startWith(isPrecedingCrop$.value)),
      cropId$?.valueChanges.pipe(startWith(cropId$.value)),
    ])
      .pipe(takeUntil(this.destroyed$))
      .subscribe(
        ([nitrogenCalculationSettingTogglesByAdvices, isPrecedingCrop, cropId]: [
          NitrogenCalculationSettingToggles[],
          boolean,
          number,
        ]) => {
          if (
            nitrogenCalculationSettingTogglesByAdvices.find((x) => x.adviceId === this.adviceId)
              ?.shouldShowAfterTornGrassToggle &&
            isPrecedingCrop
          ) {
            this.formGroup.controls.nitrogenCalculationSettings
              ?.get('isAfterTornGrass')
              ?.setValue(cropId === this.env.GRASSLAND_CROP_ID);
          }
        },
      );
  }

  setShouldBeUsedInLegalStandardAutomatically() {
    this.store
      .pipe(select(selectCropFieldAdvicesByIds([this.fertilizationSetting.adviceId])))
      .pipe(takeUntil(this.destroyed$))
      .subscribe((cropFieldAdvices: CropFieldAdvice[]) => {
        if (
          cropFieldAdvices.length > 0 &&
          this.data.nonProductiveCrops?.some((c) => c.id === cropFieldAdvices[0].crop?.id)
        ) {
          this.formGroup.get('shouldBeUsedInLegalStandard').setValue(false);
          this.formGroup.get('shouldBeUsedInLegalStandard').disable();
        }
      });
  }

  isPrecedingCrop() {
    return this.formGroup.get('isPrecedingCrop').value as boolean;
  }

  isGreenManure() {
    return this.formGroup.get('isGreenManure').value as boolean;
  }

  getPrecedingCropFormGroup(): FormGroup {
    return this.formGroup.controls.precedingCropSettings as FormGroup;
  }

  getGreenManureFormGroup(): FormGroup {
    return this.formGroup.controls.greenManureSettings as FormGroup;
  }

  getNitrogenCalculationSettingsFormGroup(): FormGroup {
    return this.formGroup.controls.nitrogenCalculationSettings as FormGroup;
  }

  private registerFormEvents() {
    this.formGroup
      .get('isGreenManure')
      .valueChanges.pipe(takeUntil(this.destroyed$))
      .subscribe((isGreenManure: boolean) => {
        this.updateGreenManureForm(isGreenManure);
      });

    this.formGroup
      .get('isPrecedingCrop')
      .valueChanges.pipe(takeUntil(this.destroyed$))
      .subscribe((isPrecedingCrop: boolean) => {
        this.updatePrecedingCropForm(isPrecedingCrop);

        this.formGroup.controls.precedingCropSettings
          .get('isNitrogenDeliveryAmountSetManually')
          ?.setValue(false, { emitEvent: false });
      });

    this.updateGreenManureForm(!!this.formGroup.get('isGreenManure').value);
    this.updatePrecedingCropForm(!!this.formGroup.get('isPrecedingCrop').value);
  }

  private updateGreenManureForm(isGreenManure: boolean) {
    if (!isGreenManure) {
      this.formGroup.controls.greenManureSettings.reset();
      this.setGreenManureSettingsValidator([]);
    } else {
      this.setGreenManureSettingsValidator([Validators.required]);
    }

    this.formGroup.controls.greenManureSettings
      .get('isNitrogenDeliveryAmountSetManually')
      ?.setValue(false, { emitEvent: false });
  }

  private updatePrecedingCropForm(isPrecedingCrop: boolean) {
    if (!isPrecedingCrop) {
      this.formGroup.controls.precedingCropSettings.reset();
      this.setPrecedingCropSettingsValidator([]);
    } else {
      this.setPrecedingCropSettingsValidator([Validators.required]);
    }
  }

  private setGreenManureSettingsValidator(newValidator: ValidatorFn[]) {
    this.formGroup.controls.greenManureSettings.get('cropId')?.setValidators(newValidator);
    this.formGroup.controls.greenManureSettings
      .get('seasonGreenManureIncorporated')
      ?.setValidators(newValidator);
    this.formGroup.controls.greenManureSettings.get('successRate')?.setValidators(newValidator);
    this.formGroup.controls.greenManureSettings
      .get('nitrogenDeliveryAmount')
      ?.setValidators([...newValidator, Validators.min(1)]);

    this.formGroup.controls.greenManureSettings.get('cropId')?.updateValueAndValidity();
    this.formGroup.controls.greenManureSettings
      .get('seasonGreenManureIncorporated')
      ?.updateValueAndValidity();
    this.formGroup.controls.greenManureSettings.get('successRate')?.updateValueAndValidity();
    this.formGroup.controls.greenManureSettings
      .get('nitrogenDeliveryAmount')
      ?.updateValueAndValidity();
  }

  private setPrecedingCropSettingsValidator(newValidator: ValidatorFn[]) {
    this.formGroup.controls.precedingCropSettings.get('cropId')?.setValidators(newValidator);
    this.formGroup.controls.precedingCropSettings
      .get('nitrogenDeliveryAmount')
      ?.setValidators(newValidator);

    this.formGroup.controls.precedingCropSettings.get('cropId')?.updateValueAndValidity();
    this.formGroup.controls.precedingCropSettings
      .get('nitrogenDeliveryAmount')
      ?.updateValueAndValidity();
  }
}
